import { useEffect, useRef, useState } from "react";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useRecordContext,
} from "ra-core";
import { AlertTriangle, BarChart3, RefreshCw, Sparkles } from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";
import { fr } from "date-fns/locale";
import ReactMarkdown from "react-markdown";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { streamSkillRun } from "@/lib/agenticClient";

import type { Contact } from "../types";

// Sprint 10.x — Rapport d'activité produit narratif IA (skill
// posthog_activity_report). Remplace la liste brute d'events PostHog dans
// l'onglet Activité de la fiche contact par un récit lisible
// (habitudes / parcours / blocages).

const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;
const SKILL_MODEL_USED = "deepseek-chat";

interface ContactActivityReportRow {
  id: string;
  contact_id: number;
  content: string;
  generated_at: string;
  generated_by: string | null;
  model_used: string | null;
  is_current: boolean;
  created_at: string;
}

type ReportState = "empty" | "generated" | "stale" | "pending" | "error";

function deriveState(
  report: ContactActivityReportRow | undefined,
  pending: boolean,
  errorMessage: string | null,
): ReportState {
  if (pending) return "pending";
  if (errorMessage) return "error";
  if (!report) return "empty";
  const age = Date.now() - new Date(report.generated_at).getTime();
  return age >= STALE_THRESHOLD_MS ? "stale" : "generated";
}

export function ContactActivityReport() {
  const record = useRecordContext<Contact>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const enabled = !!record?.id;
  const query = useGetList<ContactActivityReportRow>(
    "contact_activity_reports",
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: "generated_at", order: "DESC" },
      filter: { contact_id: record?.id, is_current: true },
    },
    { enabled },
  );

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  if (!record) return null;

  const report = query.data?.[0];
  const state = deriveState(report, pending, errorMessage);

  const handleGenerate = async () => {
    if (!record.id) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setPending(true);
    setErrorMessage(null);
    let content: string | null = null;

    try {
      for await (const ev of streamSkillRun(
        "posthog_activity_report",
        { contact_id: record.id },
        { signal: ctrl.signal },
      )) {
        if (ev.event === "run.done") {
          const out = (ev.data as { output?: { content?: string } }).output;
          content = out?.content ?? null;
        } else if (ev.event === "run.error") {
          throw new Error(
            (ev.data as { error?: string }).error ?? "skill_run_failed",
          );
        }
      }
    } catch (err) {
      if (ctrl.signal.aborted) return;
      const msg = (err as Error).message;
      setErrorMessage(msg);
      notify(`Génération du rapport échouée : ${msg}`, { type: "error" });
      setPending(false);
      return;
    }

    if (!content) {
      const msg = "Le rapport généré est vide.";
      setErrorMessage(msg);
      notify(msg, { type: "warning" });
      setPending(false);
      return;
    }

    try {
      await dataProvider.create("contact_activity_reports", {
        data: {
          contact_id: record.id,
          content,
          model_used: SKILL_MODEL_USED,
          is_current: true,
        },
      });
      await query.refetch();
    } catch (err) {
      notify(`Rapport généré mais non sauvegardé : ${(err as Error).message}`, {
        type: "warning",
      });
    } finally {
      setPending(false);
    }
  };

  const isErrorVariant = state === "error";

  const containerVariant = isErrorVariant
    ? "from-red-50 to-red-100/60 border-red-200 dark:from-red-950/30 dark:to-red-900/20 dark:border-red-900/40"
    : "from-violet-50 to-violet-100/50 border-violet-200 dark:from-violet-950/30 dark:to-violet-900/20 dark:border-violet-900/40";

  const iconWrapVariant = isErrorVariant
    ? "bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300"
    : "bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300";

  const labelVariant = isErrorVariant
    ? "text-red-800 dark:text-red-300"
    : "text-violet-800 dark:text-violet-300";

  return (
    <Card
      className={cn(
        "p-4 md:p-5 flex flex-col gap-3 bg-gradient-to-b border",
        containerVariant,
      )}
      data-testid="contact-activity-report"
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "size-8 shrink-0 rounded-lg flex items-center justify-center",
            iconWrapVariant,
          )}
        >
          {state === "error" ? (
            <AlertTriangle className="size-4" />
          ) : (
            <BarChart3 className="size-4" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div
            className={cn(
              "text-[11px] font-semibold uppercase tracking-wider inline-flex items-center gap-1.5",
              labelVariant,
            )}
          >
            <span>
              {state === "error"
                ? "Rapport d'activité · erreur"
                : state === "stale"
                  ? "Rapport d'activité · obsolète"
                  : "Rapport d'activité IA"}
            </span>
            {state === "stale" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100/80 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 px-1.5 py-0.5 text-[10px] font-medium normal-case tracking-normal">
                <AlertTriangle className="size-2.5" />
                {">"}24h
              </span>
            )}
          </div>
          <p className="text-[12.5px] text-muted-foreground mt-0.5">
            Synthèse narrative des events PostHog : habitudes, parcours,
            blocages.
          </p>
        </div>
      </div>

      {state === "pending" && <PendingSkeleton />}

      {state === "empty" && (
        <EmptyCta onGenerate={handleGenerate} disabled={pending} />
      )}

      {state === "error" && (
        <ErrorBody message={errorMessage!} onRetry={handleGenerate} />
      )}

      {(state === "generated" || state === "stale") && report && (
        <GeneratedBody
          report={report}
          onRegenerate={handleGenerate}
          isStale={state === "stale"}
        />
      )}
    </Card>
  );
}

function PendingSkeleton() {
  return (
    <div className="flex flex-col gap-1.5 px-1">
      <div className="h-3 w-[94%] rounded bg-violet-200/60 dark:bg-violet-800/40 animate-pulse" />
      <div className="h-3 w-[82%] rounded bg-violet-200/60 dark:bg-violet-800/40 animate-pulse" />
      <div className="h-3 w-[88%] rounded bg-violet-200/60 dark:bg-violet-800/40 animate-pulse" />
      <div className="h-3 w-[60%] rounded bg-violet-200/60 dark:bg-violet-800/40 animate-pulse" />
    </div>
  );
}

function EmptyCta({
  onGenerate,
  disabled,
}: {
  onGenerate: () => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <p className="text-[13px] text-foreground/90 flex-1 min-w-0">
        Aucun rapport généré pour ce contact. La synthèse remplace la liste
        brute d'events PostHog par un récit en français.
      </p>
      <Button
        type="button"
        size="sm"
        onClick={onGenerate}
        disabled={disabled}
        className="gap-1.5"
      >
        <Sparkles className="size-3.5" />
        Générer le rapport
      </Button>
    </div>
  );
}

function ErrorBody({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div>
      <p className="text-[13px] text-foreground/90 leading-relaxed">
        Impossible de générer le rapport : <strong>{message}</strong>
      </p>
      <div className="mt-2.5">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="gap-1.5 h-7 px-2.5 text-xs"
          onClick={onRetry}
        >
          <RefreshCw className="size-3" />
          Réessayer
        </Button>
      </div>
    </div>
  );
}

function GeneratedBody({
  report,
  onRegenerate,
  isStale,
}: {
  report: ContactActivityReportRow;
  onRegenerate: () => void;
  isStale: boolean;
}) {
  const rel = formatDistanceToNowStrict(new Date(report.generated_at), {
    addSuffix: true,
    locale: fr,
  });
  return (
    <>
      <div className="brief-markdown text-[13.5px] text-foreground/90 leading-relaxed">
        <ReactMarkdown
          components={{
            h1: ({ children }) => (
              <h3 className="text-[14px] font-semibold text-foreground mt-3 mb-1 first:mt-0">
                {children}
              </h3>
            ),
            h2: ({ children }) => (
              <h3 className="text-[14px] font-semibold text-foreground mt-3 mb-1 first:mt-0">
                {children}
              </h3>
            ),
            h3: ({ children }) => (
              <h4 className="text-[13px] font-semibold text-foreground mt-3 mb-1 first:mt-0">
                {children}
              </h4>
            ),
            p: ({ children }) => (
              <p className="my-1 first:mt-0 last:mb-0">{children}</p>
            ),
            ul: ({ children }) => (
              <ul className="my-1 space-y-0.5 list-disc pl-5 marker:text-violet-500/70">
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol className="my-1 space-y-0.5 list-decimal pl-5">{children}</ol>
            ),
            li: ({ children }) => <li className="pl-0.5">{children}</li>,
            strong: ({ children }) => (
              <strong className="font-semibold text-foreground">
                {children}
              </strong>
            ),
            a: ({ href, children }) => (
              <a
                href={href}
                className="text-violet-700 hover:underline dark:text-violet-300"
              >
                {children}
              </a>
            ),
            code: ({ children }) => (
              <code className="rounded bg-violet-100/70 dark:bg-violet-900/40 px-1 py-0.5 text-[12px] font-mono">
                {children}
              </code>
            ),
          }}
        >
          {report.content}
        </ReactMarkdown>
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-muted-foreground">
        <span>Généré {rel}</span>
        {report.model_used && (
          <>
            <span aria-hidden className="text-muted-foreground/50">
              ·
            </span>
            <span>{report.model_used}</span>
          </>
        )}
        <span aria-hidden className="text-muted-foreground/50">
          ·
        </span>
        <Button
          type="button"
          size="sm"
          variant={isStale ? "default" : "ghost"}
          className={cn("gap-1.5 h-7 px-2 text-xs", isStale && "h-7 px-2.5")}
          onClick={onRegenerate}
        >
          <RefreshCw className="size-3" />
          Régénérer
        </Button>
      </div>
    </>
  );
}
